<template>
  <div class="page-mobile-app">
    <landing-header />

    <main>
      <section class="hero-mobile">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-app">Outil Terrain Haute Performance</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              L'application qui suit <br />
              <span class="gradient-text">vos techniciens partout</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Pensée pour le froid, le chaud et les zones sans réseau. Donnez à vos équipes un outil fiable, opérationnel et conçu pour les réalités du terrain.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <div class="offline-tag">
                <i class="fa-solid fa-cloud-arrow-up"></i> Synchronisation Auto
              </div>
            </div>
          </div>
          
          <div class="lg:w-1/2 flex justify-center">
            <div class="app-visual-container">
              <div class="phone-frame shadow-2xl">
                <div class="phone-screen">
                  <div class="app-nav">Intervention #4502</div>
                  <div class="app-content-demo">
                    <div class="demo-card active">
                      <p class="label">Statut</p>
                      <p class="val text-orange-600 font-bold">En cours d'exécution</p>
                    </div>
                    <div class="demo-card">
                      <p class="label">Client</p>
                      <p class="val font-bold">Hôtel de la Marine</p>
                    </div>
                    <div class="btn-demo-action">Signer le rapport</div>
                  </div>
                </div>
              </div>
              <div class="floating-offline">
                <i class="fa-solid fa-wifi-slash"></i>
                <span>100% Offline</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 bg-gray-900 text-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col md:flex-row items-center gap-12">
            <div class="md:w-1/3">
              <h2 class="text-3xl font-bold mb-4">Fonctionnement hors connexion</h2>
              <p class="text-gray-400">Aucune perte d'information, aucun blocage opérationnel. Le technicien travaille, Termoflow synchronise dès le retour du signal.</p>
            </div>
            <div class="md:w-2/3 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="offline-card">Consultation clients</div>
              <div class="offline-card">Saisie rapports</div>
              <div class="offline-card">Accès docs tech</div>
              <div class="offline-card">Génération CERFA</div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 space-y-32">
          
          <div class="flex flex-col lg:flex-row items-center gap-16">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Tout l'historique dans la poche</h2>
              <p class="text-gray-600 mb-6">Fini les appels au bureau pour demander une notice ou l'historique d'un appareil. Le technicien accède en 1 clic aux coordonnées, contrats et documentations.</p>
              <div class="feature-grid-mini">
                <div class="mini-card">
                  <i class="fa-solid fa-list-check"></i>
                  <span>Rapports guidés</span>
                </div>
                <div class="mini-card">
                  <i class="fa-solid fa-box-open"></i>
                  <span>Stock camion</span>
                </div>
                <div class="mini-card">
                  <i class="fa-solid fa-file-signature"></i>
                  <span>Signature client</span>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2 bg-orange-50 p-12 rounded-[3rem] border border-orange-100 flex justify-center">
              <i class="fa-solid fa-mobile-screen-button text-[120px] text-orange-200"></i>
            </div>
          </div>

          <div class="flex flex-col lg:flex-row-reverse items-center gap-16">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Zéro administratif au retour au dépôt</h2>
              <p class="text-gray-600 mb-6">CERFA générés sur place, matériel consommé déduit du stock camion, signature enregistrée. Le travail est terminé quand le technicien quitte le site.</p>
              <ul class="check-list-modern">
                <li><i class="fa-solid fa-check"></i> Gestion des mouvements de gaz</li>
                <li><i class="fa-solid fa-check"></i> État des détecteurs de fuite</li>
                <li><i class="fa-solid fa-check"></i> Validation immédiate des documents</li>
              </ul>
            </div>
            <div class="lg:w-1/2">
               <div class="stock-demo-box">
                  <div class="stock-item">Fluide R10 (5kg) <span class="tag">Consommé</span></div>
                  <div class="stock-item">Fluide R32 (2.5kg) <span class="tag">Consommé</span></div>
               </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-4 text-center">
          <h2 class="text-3xl font-bold text-gray-900 mb-12">Le terrain devient votre source de données fiable</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div class="benefit-card-v2">
              <div class="benefit-icon"><i class="text-orange-500 fa-solid fa-clock"></i></div>
              <h4>Gain de temps</h4>
              <p>Sur chaque intervention</p>
            </div>
            <div class="benefit-card-v2">
              <div class="benefit-icon"><i class="text-orange-500 fa-solid fa-circle-check"></i></div>
              <h4>Zéro oubli</h4>
              <p>Processus guidés</p>
            </div>
            <div class="benefit-card-v2">
              <div class="benefit-icon"><i class="text-orange-500 fa-solid fa-user"></i></div>
              <h4>Autonomie</h4>
              <p>Accès total aux données</p>
            </div>
            <div class="benefit-card-v2">
              <div class="benefit-icon"><i class="text-orange-500 fa-solid fa-chart-pie"></i></div>
              <h4>Pilotage</h4>
              <p>Data temps réel</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 text-center">
        <div class="max-w-2xl mx-auto px-4">
          <h2 class="text-4xl font-black text-gray-900 mb-8">Passez au terrain 2.0</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <button class="btn-primary-large">Essayer l'application</button>
             <a href="/fonctionnalites" class="btn-outline-large">Voir les fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'MobileAppPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* --- TEXTE --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO --- */
.hero-mobile {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at top right, #fff7ed, transparent 35%);
}

.badge-app {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #fef2f2;
  color: #dc2626;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
}

.offline-tag {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  padding: 0.5rem 1.2rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  font-weight: 700;
  font-size: 0.85rem;
  color: #16a34a;
}

/* --- VISUEL APP --- */
.app-visual-container { position: relative; }
.phone-frame {
  width: 280px;
  height: 560px;
  background: #1f2937;
  border-radius: 3rem;
  padding: 0.8rem;
  border: 4px solid #374151;
}

.phone-screen {
  background: #f9fafb;
  width: 100%;
  height: 100%;
  border-radius: 2.2rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-nav {
  background: white;
  padding: 1.5rem 1rem;
  font-weight: 900;
  font-size: 0.9rem;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
}

.app-content-demo { padding: 1rem; }
.demo-card {
  background: white;
  padding: 1rem;
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
}
.demo-card.active { border-left: 4px solid #f97316; }
.demo-card .label { font-size: 0.65rem; color: #9ca3af; text-transform: uppercase; font-weight: 800; }
.demo-card .val { font-size: 0.85rem; }

.btn-demo-action {
  margin-top: 2rem;
  background: #1f2937;
  color: white;
  padding: 1rem;
  border-radius: 0.75rem;
  text-align: center;
  font-weight: 800;
  font-size: 0.8rem;
}

.floating-offline {
  position: absolute;
  top: 4rem;
  right: -2rem;
  background: #1f2937;
  color: white;
  padding: 1rem;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.2);
}

.floating-offline i { font-size: 1.5rem; color: #f97316; }
.floating-offline span { font-weight: 800; font-size: 0.7rem; }

/* --- SECTION OFFLINE --- */
.offline-card {
  background: #374151;
  padding: 1.5rem;
  border-radius: 1rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;
}
.offline-card:hover { transform: scale(1.05); color: #f97316; }

/* --- FEATURES GRID --- */
.feature-grid-mini { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-top: 2rem; }
.mini-card {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-weight: 800;
  font-size: 0.7rem;
}
.mini-card i { font-size: 1.5rem; color: #f97316; }

.check-list-modern { list-style: none; padding: 0; }
.check-list-modern li { display: flex; align-items: center; gap: 0.75rem; font-weight: 700; margin-bottom: 1rem; color: #4b5563; }
.check-list-modern li i { color: #f97316; }

.stock-demo-box { background: #1f2937; padding: 2rem; border-radius: 2rem; color: white; }
.stock-item {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #374151;
  font-weight: 700;
  font-size: 0.9rem;
}
.stock-item .tag { background: #16a34a; font-size: 0.6rem; padding: 0.2rem 0.6rem; border-radius: 9999px; }

/* --- BENEFIT CARDS --- */
.benefit-card-v2 {
  background: white;
  padding: 2.5rem 1.5rem;
  border-radius: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
  border: 1px solid #f3f4f6;
}
.benefit-icon { font-size: 2.5rem; margin-bottom: 1rem; }
.benefit-card-v2 h4 { font-weight: 900; margin-bottom: 0.5rem; }
.benefit-card-v2 p { color: #6b7280; font-size: 0.9rem; }

/* --- BUTTONS --- */
.btn-primary { background: linear-gradient(to right, #f97316, #ef4444); color: white; padding: 1rem 2rem; border-radius: 9999px; font-weight: 700; border: none; }
.btn-primary-large { background: #f97316; color: white; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; border: none; font-size: 1.1rem; }
.btn-outline-large { border: 2px solid #e5e7eb; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; color: #1f2937; }

@media (max-width: 1024px) {
  .hero-mobile { text-align: center; padding-top: 8rem; }
  .hero-mobile .flex { flex-direction: column; }
  .app-visual-container { margin-top: 4rem; }
}
</style>